<template>
  <div class="shipment-event-section-header">
    <div class="shipment-event-section-header-left">
      <div class="flex flex-gap-1 align-items-center">
        <h2
          :id="id"
          tabindex="-1"
          :class="{ 'visually-hidden': visibleTabs.length > 1 }"
        >
          {{ activeTab.header }}
        </h2>

        <RygenTabs
          v-if="visibleTabs.length > 1"
          v-model:active-tab-key="activeTabKey"
          :tabs="tabs"
        />

        <template v-if="activeTabKey === 'EVENTS'">
          <span
            v-if="p44TenderAccepted || capacityProviderUpdateReceived"
            class="badge"
          >
            {{ t('events.tracking') }}
          </span>

          <span v-if="checkCallStatus.data?.active" class="badge">
            {{ t('carrierPortal.checkCallReminders.enabled') }}

            <InfoIcon
              icon="pi pi-info-circle"
              :tooltip="checkCallReminderHelptext"
            />
          </span>
        </template>

        <LoadingIndicator :loading="loading" />
      </div>
    </div>

    <div class="shipment-event-section-header-right">
      <template v-if="activeTabKey === 'EVENTS'">
        <ExpandButton
          v-show="hasItemsInTimeline"
          v-model="isShowingTimeline"
          target-id="shipment-timeline"
          :false-label="
            t('common.showParam', { param: t('events.eventsAndIncidents') })
          "
          :true-label="
            t('common.hideParam', { param: t('events.eventsAndIncidents') })
          "
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { DateTime } from 'luxon';
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useRygenTabs } from '@rygen/rygen-ui/composables';
import { renderDateTime } from '@rygen/rygen-ui/i18n';
import { Tab, TenderMethod, UUID } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import {
  ShipmentEvent,
  ShipmentEventSource,
  ShipmentServiceIncident,
  TenderRequest,
  TenderResponseCode,
} from '@/model';
import { useCheckCallStore } from '@/stores/CheckCall';

import ExpandButton from '@rygen/rygen-ui/components/button/ExpandButton.vue';
import InfoIcon from '@rygen/rygen-ui/components/ui/InfoIcon.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';
import RygenTabs from '@rygen/rygen-ui/components/ui/RygenTabs.vue';

export type TabKey = 'EVENTS' | 'COMMENTS';
export interface ShipmentEventSectionTab extends Tab<TabKey> {}

const P44_TENDER_METHODS = [TenderMethod.P44, TenderMethod.P44_VOLUME_LTL];

const activeTabKey = defineModel('activeTabKey', {
  type: String as PropType<TabKey>,
  default: () => 'EVENTS',
});

const isShowingTimeline = defineModel('showingTimeline', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  tabs: {
    type: Array as PropType<ShipmentEventSectionTab[]>,
    default: () => [],
  },
  tabStateKey: {
    type: String as PropType<UUID>,
    required: true,
  },
  shipmentEvents: {
    type: Array as PropType<ShipmentEvent[]>,
    default: () => [],
  },
  shipmentServiceIncidents: {
    type: Array as PropType<ShipmentServiceIncident[]>,
    default: () => [],
  },
  tenderRequests: {
    type: Array as PropType<TenderRequest[]>,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  tabs,
  tabStateKey,
  shipmentEvents,
  shipmentServiceIncidents,
  tenderRequests,
  loading,
} = toRefs(props);

const { checkCallStatus } = useCheckCallStore();
const { t } = useI18n();
const { activeTab, visibleTabs } = useRygenTabs(tabStateKey);

const p44TenderAccepted = computed(() => {
  return tenderRequests.value
    .map((r) => r.tenderResponse)
    .filter(notNull)
    .find(
      (r) =>
        r.responseMethod &&
        P44_TENDER_METHODS.includes(r.responseMethod) &&
        r.responseCode === TenderResponseCode.RESERVATION_ACCEPTED,
    );
});

const capacityProviderUpdateReceived = computed(() => {
  return shipmentEvents.value.some(
    (r) => r.source === ShipmentEventSource.CAPACITY_PROVIDER,
  );
});

const checkCallEmailString = computed(() => {
  const emails = checkCallStatus.value.data?.latestRecord?.context['emails'] as
    string[] | undefined;
  return emails?.join(', ') ?? '';
});

const latestCheckCallDateTimeString = computed(() => {
  const date = checkCallStatus.value.data?.latestRecord?.changeDate;
  const luxonDate = date ? DateTime.fromJSDate(date) : undefined;
  return renderDateTime(luxonDate);
});

const checkCallReminderHelptext = computed(() => {
  return t('carrierPortal.checkCallReminders.helptext', {
    email: checkCallEmailString.value,
    time: latestCheckCallDateTimeString.value,
  });
});

const nonMilestoneEvents = computed(() =>
  shipmentEvents.value.filter((e) => !e.isMilestone),
);

const hasItemsInTimeline = computed(() => {
  return !!(
    nonMilestoneEvents.value.length ||
    shipmentServiceIncidents.value.length ||
    tenderRequests.value.length
  );
});
</script>

<style>
.shipment-event-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--gap-1);
  width: 100%;

  .shipment-event-section-header-left {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);

    .badge {
      display: flex;
      align-items: center;
      gap: var(--sp-2);
      color: var(--success-color);
      border: 1px solid var(--success-color);
      border-radius: var(--p-border-radius-sm);
      font-size: var(--p-button-sm-font-size);
      padding: var(--p-button-sm-padding-y) var(--p-button-sm-padding-x);

      .info-icon-container {
        display: flex;
      }
    }
  }

  .shipment-event-section-header-right {
    display: flex;
    gap: var(--gap-1);
    align-items: center;
  }
}
</style>
